import type { StreamEntry } from "./history/types.js";
import type { TranscriptInvariantReporter } from "../../transcript-invariant.js";

// How long a live turn may show nothing of itself while its agent's stream holds tool calls.
export const NO_VISIBLE_PROGRESS_MS = 20_000;

export type ProgressSurface = "agent" | "work_session";

export interface TurnProgress {
  turnKey: string;
  toolCalls: number;
  shownRows: number;
}

// One prompt seen through two stores (agent stream, session page): same entry, or same message.
function samePrompt(a: StreamEntry, b: StreamEntry): boolean {
  if (a.id === b.id) return true;
  return (
    a.kind === "user_message" &&
    b.kind === "user_message" &&
    !!a.messageId &&
    a.messageId === b.messageId
  );
}

export function showsPrompt(shown: readonly StreamEntry[], prompt: StreamEntry): boolean {
  return shown.some((e) => samePrompt(e, prompt));
}

function lastPromptIndex(entries: readonly StreamEntry[]): number {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (entries[i].kind === "user_message") return i;
  }
  return -1;
}

// The turn's tool calls on the agent's own stream, and the rows the surface shows after its prompt.
// `ownsTurn` false: the live prompt is provably another surface's (another session, a DM), not a stall.
export function turnProgress(
  agentEntries: readonly StreamEntry[],
  shownEntries: readonly StreamEntry[],
  ownsTurn: (prompt: StreamEntry) => boolean = () => true,
): TurnProgress | undefined {
  const at = lastPromptIndex(agentEntries);
  if (at < 0) return undefined;
  const prompt = agentEntries[at];
  if (!ownsTurn(prompt)) return undefined;
  let toolCalls = 0;
  for (let i = at + 1; i < agentEntries.length; i += 1) {
    if (agentEntries[i].kind === "tool_call") toolCalls += 1;
  }
  const shownAt = shownEntries.findLastIndex((e) => samePrompt(e, prompt));
  return {
    turnKey: prompt.id,
    toolCalls,
    shownRows: shownAt < 0 ? 0 : shownEntries.length - shownAt - 1,
  };
}

export function isStalled(p: TurnProgress | undefined): p is TurnProgress {
  return !!p && p.toolCalls > 0 && p.shownRows === 0;
}

export type TurnStallSender = (
  agentId: string,
  surface: ProgressSurface,
  p: TurnProgress,
) => boolean;

export const turnStallSender =
  (invariants: TranscriptInvariantReporter): TurnStallSender =>
  (agentId, surface, p) =>
    invariants.report(
      "turn_without_visible_progress",
      { agentId, surface, toolCalls: p.toolCalls },
      Date.now(),
      p.turnKey,
    );

// Once per turn: a stall that clears and returns within the turn is the same finding. Owned by
// its conversation store (cleared with it at sign-out); bounded, the oldest turns leave first.
export const MAX_REPORTED_TURNS = 500;

export class TurnProgressReports {
  private readonly reported = new Set<string>();

  constructor(
    private readonly send: TurnStallSender,
    private readonly maxTurns = MAX_REPORTED_TURNS,
  ) {}

  report(agentId: string, surface: ProgressSurface, p: TurnProgress): boolean {
    const key = `${agentId}\u0000${p.turnKey}`;
    if (this.reported.has(key)) return false;
    if (!this.send(agentId, surface, p)) return false;
    this.reported.add(key);
    if (this.reported.size > this.maxTurns) {
      const oldest = this.reported.values().next();
      if (!oldest.done) this.reported.delete(oldest.value);
    }
    return true;
  }

  get size(): number {
    return this.reported.size;
  }

  clear(): void {
    this.reported.clear();
  }
}
